/**
 * Pemformatan tampilan: rupiah, tanggal Indonesia (zona Asia/Jakarta), angka,
 * persentase, dan label status dari enum backend.
 *
 * Semua tanggal dari API berbentuk ISO string (serialkan() -> Date.toISOString()).
 * Kolom @db.Date diserialkan sebagai ISO tengah malam UTC; saat ditampilkan
 * dengan zona Asia/Jakarta hasilnya benar (UTC+7 masih di hari yang sama).
 */

export const ZONA_WAKTU = 'Asia/Jakarta'
export const LOCALE_ID = 'id-ID'

const formatterAngkaCache = new Map<string, Intl.NumberFormat>()

function formatterAngka(minimum: number, maksimum: number): Intl.NumberFormat {
  const kunci = `${minimum}-${maksimum}`
  let f = formatterAngkaCache.get(kunci)
  if (!f) {
    f = new Intl.NumberFormat(LOCALE_ID, {
      minimumFractionDigits: minimum,
      maximumFractionDigits: maksimum,
    })
    formatterAngkaCache.set(kunci, f)
  }
  return f
}

function keAngka(nilai: number | string | null | undefined): number | null {
  if (nilai === null || nilai === undefined) return null
  if (typeof nilai === 'number') return Number.isFinite(nilai) ? nilai : null
  const bersih = nilai.replace(/\./g, '').replace(',', '.').trim()
  const n = Number(bersih)
  return Number.isFinite(n) ? n : null
}

/** 1234567 -> "Rp1.234.567" */
export function formatRupiah(nilai: number | string | null | undefined): string {
  const n = keAngka(nilai)
  if (n === null) return 'Rp0'
  const negatif = n < 0
  const teks = `Rp${formatterAngka(0, 0).format(Math.abs(Math.round(n)))}`
  return negatif ? `-${teks}` : teks
}

/** 1234567 -> "1.234.567" */
export function formatAngka(nilai: number | string | null | undefined, desimal = 0): string {
  const n = keAngka(nilai)
  if (n === null) return '-'
  return formatterAngka(desimal, desimal).format(n)
}

/** 0.853 -> "85,3%" */
export function formatPersen(nilai: number | string | null | undefined, desimal = 1): string {
  const n = keAngka(nilai)
  if (n === null) return '-'
  return `${formatterAngka(desimal, desimal).format(n)}%`
}

/** Persentase dari rasio pecahan: 85.3 -> "85,3%" bila `rasio` false. */
export function formatPersenRasio(
  pembilang: number | null | undefined,
  penyebut: number | null | undefined,
  desimal = 1,
): string {
  const a = keAngka(pembilang ?? null)
  const b = keAngka(penyebut ?? null)
  if (a === null || b === null || b === 0) return '-'
  return formatPersen((a / b) * 100, desimal)
}

function keTanggal(iso: string | number | Date | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === '') return null
  if (iso instanceof Date) return Number.isNaN(iso.getTime()) ? null : iso
  // Dukungan nilai 'YYYY-MM-DD' (kolom @db.Date) tanpa pergeseran zona.
  if (typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(Date.UTC(y, m - 1, d))
  }
  const t = new Date(iso)
  return Number.isNaN(t.getTime()) ? null : t
}

/** "2026-10-11" -> "11 Oktober 2026" */
export function formatTanggal(iso: string | number | Date | null | undefined): string {
  const t = keTanggal(iso)
  if (!t) return '-'
  return new Intl.DateTimeFormat(LOCALE_ID, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: ZONA_WAKTU,
  }).format(t)
}

/** Versi ringkas: "11 Okt 2026" */
export function formatTanggalSingkat(iso: string | number | Date | null | undefined): string {
  const t = keTanggal(iso)
  if (!t) return '-'
  return new Intl.DateTimeFormat(LOCALE_ID, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: ZONA_WAKTU,
  }).format(t)
}

/** "11 Oktober 2026, 14.30" */
export function formatTanggalJam(iso: string | number | Date | null | undefined): string {
  const t = keTanggal(iso)
  if (!t) return '-'
  return new Intl.DateTimeFormat(LOCALE_ID, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: ZONA_WAKTU,
  }).format(t)
}

/** "14.30" */
export function formatJam(iso: string | number | Date | null | undefined): string {
  const t = keTanggal(iso)
  if (!t) return '-'
  return new Intl.DateTimeFormat(LOCALE_ID, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: ZONA_WAKTU,
  }).format(t)
}

/** Tanggal hari ini (YYYY-MM-DD) menurut zona Asia/Jakarta. */
export function tanggalHariIni(): string {
  const bagian = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: ZONA_WAKTU,
  }).format(new Date())
  return bagian
}

/** Awal bulan berjalan (YYYY-MM-DD) menurut zona Asia/Jakarta. */
export function awalBulanIni(): string {
  return `${tanggalHariIni().slice(0, 7)}-01`
}

/** Awal tahun berjalan (YYYY-01-01). */
export function awalTahunIni(): string {
  return `${tanggalHariIni().slice(0, 4)}-01-01`
}

/** ISO string -> nilai untuk <input type="date">. */
export function keInputTanggal(iso: string | null | undefined): string {
  const t = keTanggal(iso ?? null)
  if (!t) return ''
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: ZONA_WAKTU,
  }).format(t)
}

// ------------------------------------------------------------------- Label status
const LABEL_STATUS: Record<string, string> = {
  // Umum
  ACTIVE: 'Aktif',
  INACTIVE: 'Tidak Aktif',
  SUSPENDED: 'Ditangguhkan',
  ARCHIVED: 'Diarsipkan',
  DRAFT: 'Draf',
  PENDING: 'Menunggu',
  PENDING_APPROVAL: 'Menunggu Persetujuan',
  PENDING_PAYMENT: 'Menunggu Pembayaran',
  APPROVED: 'Disetujui',
  REJECTED: 'Ditolak',
  CANCELLED: 'Dibatalkan',
  COMPLETED: 'Selesai',
  CLOSED: 'Ditutup',
  OPEN: 'Terbuka',
  LOCKED: 'Terkunci',
  EXPIRED: 'Kedaluwarsa',
  FAILED: 'Gagal',
  NEEDS_REVIEW: 'Perlu Peninjauan',
  NEEDS_REVISION: 'Perlu Revisi',
  IN_PROGRESS: 'Sedang Diproses',
  APPLIED: 'Diterapkan',

  // Tagihan & pembayaran
  UNPAID: 'Belum Dibayar',
  PARTIALLY_PAID: 'Dibayar Sebagian',
  PAID: 'Lunas',
  OVERDUE: 'Jatuh Tempo',
  IN_CORRECTION: 'Dalam Koreksi',
  VERIFIED: 'Terverifikasi',
  POSTED: 'Terposting',
  RECONCILED: 'Terekonsiliasi',
  SUBMITTED: 'Diajukan',
  REVIEWED: 'Ditinjau',

  // Jurnal & transaksi
  REVERSED: 'Dibalik',
  PAID_OUT: 'Sudah Dibayar',

  // Anggaran
  ACTIVATE: 'Aktif',

  // Kas kecil & pencairan
  DISBURSED: 'Dicairkan',
  RECEIVED: 'Diterima',
  MATCHED: 'Cocok',
  USED: 'Terpakai',
  ACCOUNTED: 'Dipertanggungjawabkan',

  // Transfer antarunit
  REQUESTED: 'Diminta',
  SENT: 'Terkirim',

  // Rekonsiliasi
  UNMATCHED: 'Belum Cocok',
  EXCLUDED: 'Dikecualikan',

  // Aset
  FULLY_DEPRECIATED: 'Tersusut Penuh',
  DISPOSED: 'Dihapusbukukan',
  SOLD: 'Dijual',
  DAMAGED: 'Rusak',

  // Review
  ACCEPTED: 'Diterima',

  // Wali
  PENDING_GUARDIAN: 'Menunggu Verifikasi Wali',
  REVOKED: 'Dicabut',

  // Santri
  CALON: 'Calon',
  AKTIF: 'Aktif',
  CUTI: 'Cuti',
  LULUS: 'Lulus',
  KELUAR: 'Keluar',
  PINDAH: 'Pindah',
  DIBERHENTIKAN: 'Diberhentikan',
  ALUMNI: 'Alumni',

  // Kehadiran
  HADIR: 'Hadir',
  IZIN: 'Izin',
  SAKIT: 'Sakit',
  TERLAMBAT: 'Terlambat',
  ALPA: 'Alpa',
  DINAS_LUAR: 'Dinas Luar',

  // Akuntansi
  ASSET: 'Aset',
  LIABILITY: 'Liabilitas',
  EQUITY: 'Aset Neto',
  REVENUE: 'Pendapatan',
  EXPENSE: 'Beban',
  CONTRA_ASSET: 'Kontra Aset',
  CONTRA_REVENUE: 'Kontra Pendapatan',
  DEBIT: 'Debit',
  CREDIT: 'Kredit',

  // Sumber dana
  UNRESTRICTED: 'Tidak Terikat',
  TEMPORARILY_RESTRICTED: 'Terikat Sementara',
  PERMANENTLY_RESTRICTED: 'Terikat Permanen',

  // Model entitas
  SINGLE_ENTITY: 'Entitas Tunggal',
  MULTI_ENTITY: 'Multi Entitas',
  SINGLE_LEGAL_ENTITY: 'Satu Badan Hukum',
  FOUNDATION_WITH_UNITS: 'Yayasan dengan Unit',
  SEPARATE_LEGAL_ENTITIES: 'Badan Hukum Terpisah',

  // Jenis unit
  YAYASAN: 'Yayasan',
  PONDOK: 'Pondok Pesantren',
  SEKOLAH: 'Sekolah',
  UNIT_LAIN: 'Unit Lain',

  // Jenis akun kas
  CASH: 'Kas',
  BANK: 'Bank',
  GATEWAY_CLEARING: 'Akun Kliring Gateway',

  // Kebijakan pengakuan pendapatan
  ON_INVOICE: 'Saat Tagihan Terbit',
  ON_PAYMENT: 'Saat Pembayaran Diterima',
  DEFERRED: 'Ditangguhkan',

  // Metode pembayaran
  BANK_TRANSFER: 'Transfer Bank',
  VIRTUAL_ACCOUNT: 'Virtual Account',
  QRIS: 'QRIS',
  EWALLET: 'Dompet Digital',
  RETAIL_OUTLET: 'Gerai Ritel',
  CHEQUE: 'Cek',
  OTHER: 'Lainnya',

  // Transaksi
  CASH_IN: 'Kas Masuk',
  CASH_OUT: 'Kas Keluar',
  BANK_IN: 'Bank Masuk',
  BANK_OUT: 'Bank Keluar',
  CASH_TO_BANK: 'Kas ke Bank',
  BANK_TO_BANK: 'Bank ke Bank',
  INTERUNIT_TRANSFER: 'Transfer Antarunit',
  SUPPLIER_PAYMENT: 'Pembayaran Pemasok',
  PAYMENT_REFUND: 'Pengembalian Dana',
  ADJUSTMENT: 'Penyesuaian',
  OPERATIONAL_EXPENSE: 'Beban Operasional',
  PETTY_CASH_USE: 'Pemakaian Kas Kecil',
  GATEWAY_SETTLEMENT: 'Penyelesaian Gateway',

  // Sumber jurnal
  OPENING_BALANCE: 'Saldo Awal',
  INVOICE: 'Tagihan',
  PAYMENT: 'Pembayaran',
  REFUND: 'Pengembalian',
  RECEIPT: 'Penerimaan',
  PURCHASE: 'Pembelian',
  PAYROLL: 'Penggajian',
  DISBURSEMENT: 'Pencairan',
  BANK_ADJUSTMENT: 'Penyesuaian Bank',
  DEPRECIATION: 'Penyusutan',
  GATEWAY_SETTLEMENT_SETTLE: 'Penyelesaian Gateway',
  REVERSAL: 'Pembalikan',
  MANUAL: 'Manual',

  // Persetujuan
  APPROVAL_PENDING: 'Menunggu Persetujuan',
}

/** Memetakan enum backend menjadi label Indonesia yang enak dibaca. */
export function labelStatus(status: string | null | undefined): string {
  if (!status) return '-'
  const kunci = String(status).trim().toUpperCase()
  if (LABEL_STATUS[kunci]) return LABEL_STATUS[kunci]
  // Fallback: Title Case dari SNAKE_CASE.
  return kunci
    .split('_')
    .map((k) => k.charAt(0) + k.slice(1).toLowerCase())
    .join(' ')
}

/** Nada warna untuk Badge berdasarkan status. */
export type NadaBadge = 'netral' | 'sukses' | 'peringatan' | 'bahaya' | 'info'

export function nadaStatus(status: string | null | undefined): NadaBadge {
  const s = String(status ?? '').toUpperCase()
  if (!s) return 'netral'
  if (['PAID', 'POSTED', 'APPROVED', 'MATCHED', 'RECONCILED', 'COMPLETED', 'ACTIVE', 'AKTIF', 'VERIFIED', 'CLOSED', 'SELESAI', 'LUNAS', 'HADIR'].includes(s)) {
    return 'sukses'
  }
  if (['OVERDUE', 'REJECTED', 'FAILED', 'CANCELLED', 'DIBERHENTIKAN', 'ALPA', 'MISSING', 'REVOKED', 'DAMAGED'].includes(s)) {
    return 'bahaya'
  }
  if (['DRAFT', 'REQUESTED', 'SENT', 'UNMATCHED', 'PENDING', 'PENDING_APPROVAL', 'SUBMITTED', 'NEEDS_REVIEW', 'NEEDS_REVISION', 'REVIEWED', 'IN_PROGRESS', 'PARTIALLY_PAID', 'CALON', 'IZIN', 'SAKIT', 'TERLAMBAT', 'CUTI'].includes(s)) {
    return 'peringatan'
  }
  return 'info'
}

/** Nama bulan dalam Bahasa Indonesia. */
export const NAMA_BULAN = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
] as const

export function labelBulan(nomor: number): string {
  const i = Math.max(0, Math.min(11, Math.round(nomor) - 1))
  return NAMA_BULAN[i] ?? String(nomor)
}
